import React, { useEffect, useState } from 'react';

import CustomLoading from '../../../util_components/custom_loading';
import DataTable from '../shared/data_table';
import LoadFailed from '../shared/load_failed';
import { sort_rows, toggle_sort } from '../shared/use_sortable';
import { TOTALS_ROUNDING_NOTE } from '../format';
import { fetch_policy_rules } from './api';
import { RULES_NOTICE, rules_error_view, rules_view } from './adapters';

/*
 * PROD-7828 R10 — calculation-rules page (/teacher-payments/rules, a hidden tab). Read-only by
 * decision (D100/2.6): no inputs or actions in the tables that change data; column-header sort is a
 * view control, not a write, so it stays (Kuldeep, every module table sorts).
 * Not month-scoped and never blocked by D97 (R10-C13). props: on_error(e, fallback).
 * One shared sort state covers every group's table (rules/adapters.js has no paging). `value` mixes
 * numbers and free text (D96 rules registry), so it sorts as text rather than guessing a type per row.
 */
const COLUMNS = [
  {
    key: 'policy_key',
    label: 'Rule',
    hideInCard: true,
    sort_key: 'policy_key',
    sort_type: 'text',
    render: (r) => <span className="tp-mono">{r.policy_key}</span>
  },
  {
    key: 'value',
    label: 'Value',
    sort_key: 'value',
    sort_type: 'text',
    render: (r) => <span className="tp-strong">{r.value}</span>
  },
  { key: 'unit', label: 'Unit', sort_key: 'unit', sort_type: 'text' },
  { key: 'description', label: 'What it does', sort_key: 'description', sort_type: 'text' },
  { key: 'prd_ref', label: 'PRD', sort_key: 'prd_ref', sort_type: 'text' },
  { key: 'decision_ref', label: 'Decision', sort_key: 'decision_ref', sort_type: 'text' },
  {
    key: 'effective_from',
    label: 'Effective from',
    sort_key: 'effective_from',
    sort_type: 'date'
  },
  { key: 'updated_by', label: 'Changed by', sort_key: 'updated_by', sort_type: 'text' }
];

const CalculationRulesTab = ({ on_error }) => {
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [view, setView] = useState({ notice: RULES_NOTICE, groups: [] });
  const [error_text, setErrorText] = useState('');
  const [rev, setRev] = useState(0);
  const [sort, setSort] = useState(null);
  const on_sort = (key) => setSort((s) => toggle_sort(s, key));

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    setErrorText('');
    fetch_policy_rules()
      .then((data) => {
        if (cancelled) return;
        setView(rules_view(data));
        setStatus('ready');
      })
      .catch((e) => {
        if (cancelled) return;
        const fallback = 'Could not load the calculation rules.';
        const v = rules_error_view(e, fallback);
        if (v.kind === 'permission') on_error(e, fallback);
        else setErrorText(v.text);
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [rev, on_error]);

  return (
    <div>
      <div className="tp-card-hd">
        <span className="tp-card-hd-title">Calculation rules</span>
        <span className="tp-card-hd-sub">The values the payment engine uses today</span>
      </div>
      <div className="tp-banner tp-banner-info" role="note">
        {view.notice}
      </div>
      <p className="tp-rp-note">{TOTALS_ROUNDING_NOTE}</p>

      {status === 'loading' ? <CustomLoading /> : null}
      {status === 'error' && error_text ? (
        <LoadFailed text={error_text} on_retry={() => setRev((r) => r + 1)} />
      ) : null}
      {status === 'ready' && !view.groups.length ? (
        <div className="tp-empty">No calculation rules were returned.</div>
      ) : null}
      {status === 'ready'
        ? view.groups.map((g) => (
            <section key={g.key} className="tp-card" aria-labelledby={`tp-rules-${g.key}`}>
              <div className="tp-card-hd">
                <span className="tp-card-hd-title" id={`tp-rules-${g.key}`}>
                  {g.label}
                </span>
              </div>
              <DataTable
                columns={COLUMNS}
                rows={sort_rows(g.rows, COLUMNS, sort)}
                rowKey={(r) => r.policy_key}
                cardTitle={(r) => <span className="tp-mono">{r.policy_key}</span>}
                sort={sort}
                on_sort={on_sort}
              />
            </section>
          ))
        : null}
    </div>
  );
};

export default CalculationRulesTab;
